import { useCallback, useState } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import { Stack, Box } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AddSessionModal from '@/components/events/detail/tabs/sessions/tab-actions/add-session.modal.tsx';
import { SESSION_TYPE, type SessionType } from '@/types/dtos/sessions/session-type.enum';
import Widget from '@/components/common/widget.tsx';
import useSlotSessions, { type SessionWithSlotDuration } from './hooks/use-slot-sessions';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';

interface SessionGroup {
  types: SessionType[];
  title: string;
  count: number;
  totalDuration: number;
  formattedDuration: string;
  share: number;
}

export default function Sessions() {
  const t = useTranslations();
  const [addSessionModalOpen, setAddSessionModalOpen] = useState(false);
  const sessions = useSlotSessions();
  const mode = useEventMode();

  const computeSessionGroups = useCallback(() => {
    const groups: SessionGroup[] = [
      {
        types: [SESSION_TYPE.ADDRESS],
        title: t('session.typeGroups.address'),
        count: 0,
        totalDuration: 0,
        formattedDuration: '0h 0min',
        share: 0,
      },
      {
        types: [SESSION_TYPE.KEYNOTE, SESSION_TYPE.TALK],
        title: t('session.typeGroups.talksKeynotes'),
        count: 0,
        totalDuration: 0,
        formattedDuration: '0h 0min',
        share: 0,
      },
      {
        types: [SESSION_TYPE.PANEL],
        title: t('session.typeGroups.panels'),
        count: 0,
        totalDuration: 0,
        formattedDuration: '0h 0min',
        share: 0,
      },
      {
        types: [SESSION_TYPE.PITCH, SESSION_TYPE.DEMONSTRATION],
        title: t('session.typeGroups.pitchesDemos'),
        count: 0,
        totalDuration: 0,
        formattedDuration: '0h 0min',
        share: 0,
      },
      {
        types: [SESSION_TYPE.WORKSHOP, SESSION_TYPE.EXERCISE],
        title: t('session.typeGroups.participation'),
        count: 0,
        totalDuration: 0,
        formattedDuration: '0h 0min',
        share: 0,
      },
      {
        types: [SESSION_TYPE.NETWORKING],
        title: t('session.typeGroups.networking'),
        count: 0,
        totalDuration: 0,
        formattedDuration: '0h 0min',
        share: 0,
      },
      {
        types: [SESSION_TYPE.OTHER],
        title: t('session.typeGroups.other'),
        count: 0,
        totalDuration: 0,
        formattedDuration: '0h 0min',
        share: 0,
      },
    ];

    const sessionsTotalCount = sessions.length;
    const updateSessionGroup = (session: SessionWithSlotDuration, group: SessionGroup) => {
      if (group.types.includes(session.type)) {
        group.count++;
        group.totalDuration += session.slotDurationMinutes ?? 0;
        group.formattedDuration = `${Math.floor(group.totalDuration / 60)}h ${group.totalDuration % 60}min`;
        const sharePercentage = (group.count / sessionsTotalCount) * 100;
        group.share = Math.round(sharePercentage);
      }
    };

    sessions.forEach((session) => {
      groups.forEach((group) => updateSessionGroup(session, group));
    });
    return groups;
  }, [sessions, t]);

  const [sessionGroups, setSessionGroups] = useState<SessionGroup[]>([]);

  // Update session groups when sessions change (render-time update)
  const [prevSessionsForGroups, setPrevSessionsForGroups] = useState(sessions);
  if (prevSessionsForGroups !== sessions) {
    setPrevSessionsForGroups(sessions);
    setSessionGroups(computeSessionGroups());
  }

  return (
    <Widget
      title={t('session.titleWithCount', { count: sessions?.length ?? 0 })}
      primaryButton={{
        label: t('common.new'),
        onClick: () => setAddSessionModalOpen(true),
        disabled: isOrganizeMode(mode),
        testId: 'add-event-session-button',
        startIcon: <AddOutlinedIcon />,
      }}
    >
      {sessionGroups.map((group) => (
        <Stack key={group.title} direction="row">
          <Box minWidth="2rem">{group.count}</Box>
          <Stack direction="row" justifyContent="space-between" width="100%">
            <Box>{group.title}</Box>
            <Box>
              {group.formattedDuration} | {group.share}%
            </Box>
          </Stack>
        </Stack>
      ))}
      <AddSessionModal open={addSessionModalOpen} setOpen={setAddSessionModalOpen} />
    </Widget>
  );
}
